import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import ResponseError from "#/types/responseError"
import buildCarrotApiRetryPolicy from "#/utils/CarrotApiRetryPolicy"

export interface MemberContentFeedbackItem {
  memberContentFeedbackId: number
  employeeId: number
  partnerAccessId?: number
  contentfulEntryId: string
  categoryEntryId: string
  contentType: string
  wasHelpful: boolean
  memberFeedbackSelections: string[]
  memberFeedbackText?: string
  createdAtUtc: string
}

export interface CreateFeedbackRequest {
  contentfulEntryId: string
  categoryEntryId: string
  contentType: string
  wasHelpful: boolean
  memberFeedbackSelections: string[]
  memberFeedbackText?: string
}

const MEMBER_CONTENT_FEEDBACK_QUERY_KEY = "memberContentFeedback"

export function useMemberContentFeedback(
  contentfulEntryId: string,
  categoryEntryId: string
): UseQueryResult<MemberContentFeedbackItem | null, ResponseError> {
  return useQuery<MemberContentFeedbackItem | null, ResponseError>(
    [MEMBER_CONTENT_FEEDBACK_QUERY_KEY, contentfulEntryId, categoryEntryId],
    () => carrotClient.getMemberContentFeedback(contentfulEntryId, categoryEntryId),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: false,
      retry: buildCarrotApiRetryPolicy(3),
      staleTime: 60000,
      enabled: !!(contentfulEntryId && categoryEntryId)
    }
  )
}

export function useCreateMemberContentFeedback(): UseMutationResult<
  MemberContentFeedbackItem,
  ResponseError,
  CreateFeedbackRequest
> {
  const queryClient = useQueryClient()

  return useMutation<MemberContentFeedbackItem, ResponseError, CreateFeedbackRequest>(
    (request) => carrotClient.createMemberContentFeedback(request),
    {
      retry: buildCarrotApiRetryPolicy(1),
      onSuccess: (data) => {
        queryClient.invalidateQueries([MEMBER_CONTENT_FEEDBACK_QUERY_KEY, data.contentfulEntryId, data.categoryEntryId])
        queryClient.setQueryData(
          [MEMBER_CONTENT_FEEDBACK_QUERY_KEY, data.contentfulEntryId, data.categoryEntryId],
          data
        )
      }
    }
  )
}
